import React, { useEffect, useState } from 'react';
import axios from 'axios';
import { useLocation, useNavigate } from 'react-router-dom';
import { API_BASE_URL } from '../config/apiconfig';

function useQuery() {
  return new URLSearchParams(useLocation().search);
}

export default function ResetPassword() {
  const query = useQuery();
  const token = query.get('token');
  const navigate = useNavigate();

  const [loading, setLoading] = useState(true);
  const [valid, setValid] = useState(false);
  const [email, setEmail] = useState('');
  const [name, setName] = useState('');
  const [password, setPassword] = useState('');
  const [confirm, setConfirm] = useState('');
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState(null);
  const [success, setSuccess] = useState(false);

  useEffect(() => {
    const run = async () => {
      setError(null);
      setLoading(true);
      setValid(false);
      setSuccess(false);

      try {
        if (!token) {
          setError('Token is missing');
          return;
        }
        const res = await axios.get(`${API_BASE_URL}/api/password-reset/validate`, {
          params: { token },
        });
        setEmail(res.data?.email || '');
        setName(res.data?.name || '');
        setValid(true);
      } catch (err) {
        setError(err?.response?.data?.error || 'Invalid or expired token');
      } finally {
        setLoading(false);
      }
    };

    run();
  }, [token]);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError(null);
    setSuccess(false);

    if (!password || password.length < 8) {
      setError('Password must be at least 8 characters');
      return;
    }
    if (password !== confirm) {
      setError('Passwords do not match');
      return;
    }

    try {
      setSubmitting(true);
      await axios.post(`${API_BASE_URL}/api/password-reset/complete`, {
        token,
        password,
      });
      setSuccess(true);
      setTimeout(() => navigate('/'), 1200);
    } catch (err) {
      setError(err?.response?.data?.error || 'Failed to reset password');
    } finally {
      setSubmitting(false);
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gradient-to-r from-blue-900 to-blue-700">
        <div className="text-white text-xl">Loading...</div>
      </div>
    );
  }

  if (!valid) {
    return (
      <div className="min-h-screen p-6 bg-gradient-to-r from-blue-900 to-blue-700 flex items-center justify-center">
        <div className="w-full max-w-md bg-white p-6 rounded-xl shadow-lg">
          <h1 className="text-2xl font-bold mb-4">Reset Password</h1>
          <div className="bg-red-50 border border-red-200 text-red-600 p-3 rounded-lg mb-4">{error}</div>
          <button
            type="button"
            onClick={() => navigate('/')}
            className="w-full py-3 rounded border border-gray-300 hover:bg-gray-50"
          >
            Back to login
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen p-6 bg-gradient-to-r from-blue-900 to-blue-700 flex items-center justify-center">
      <div className="w-full max-w-md bg-white p-6 rounded-xl shadow-lg">
        <h1 className="text-2xl font-bold mb-2">Reset Password</h1>
        <p className="text-sm text-gray-600 mb-4">{name ? `${name} (${email})` : email}</p>

        {error && (
          <div className="bg-red-50 border border-red-200 text-red-600 p-3 rounded-lg mb-4">{error}</div>
        )}
        {success && (
          <div className="bg-green-50 border border-green-200 text-green-700 p-3 rounded-lg mb-4">
            Password reset successfully. Redirecting to login...
          </div>
        )}

        <form onSubmit={handleSubmit}>
          <div className="mb-4">
            <label className="block text-sm mb-1">New password</label>
            <input
              type="password"
              className="border rounded w-full p-3"
              value={password}
              onChange={(e) => setPassword(e.target.value)}
              placeholder="At least 8 characters"
              required
              minLength={8}
            />
          </div>

          <div className="mb-6">
            <label className="block text-sm mb-1">Confirm password</label>
            <input
              type="password"
              className="border rounded w-full p-3"
              value={confirm}
              onChange={(e) => setConfirm(e.target.value)}
              required
              minLength={8}
            />
            {password && confirm && password !== confirm && (
              <p className="text-red-600 text-sm mt-1">Passwords do not match</p>
            )}
          </div>

          <button
            type="submit"
            disabled={submitting || !password || password !== confirm || password.length < 8}
            className={`w-full py-3 rounded text-white ${submitting || !password || password !== confirm || password.length < 8 ? 'bg-gray-400' : 'bg-blue-600 hover:bg-blue-700'}`}
          >
            {submitting ? 'Resetting...' : 'Reset password'}
          </button>
        </form>
      </div>
    </div>
  );
}
